import * as api from '../../../api';

import moment from 'moment';
import styled from 'styled-components';

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Typography,
  Card,
  VStack,
  HStack,
  Avatar,
  Divider,
  Button,
} from '@vivaahealth/design-system';

import AlertMessage from '../AlertMessage';
import VisitInstructionsModal from './visitInstructionsModal';

import { constructAddress } from '../../../utils/practice';
import { getProviderNameFriendly } from '../../../utils/provider';
import { getPrevisitAsset } from '../../../utils';
import { generateGoogleMapsDirectionURL } from '../../../utils/location';
import { formatProviderSpecialties } from 'common/dist/utils/provider';

const AppointmentDetailsCard = (
  args: api.DigitalCheckIn.DigitalCheckInDetails & {
    isCheckingIn: boolean;
  },
) => {
  const { t } = useTranslation('digitalCheckIn');
  const { encounter, practice, provider, isCheckingIn } = args;

  const [visitInstructionModalIsOpen, setVisitInstructionModalIsOpen] =
    useState(false);

  const formattedName = getProviderNameFriendly(provider, [
    'firstName',
    'lastName',
    'suffix',
  ]);
  const formattedAddress = constructAddress(practice);

  return (
    <>
      {visitInstructionModalIsOpen && (
        <VisitInstructionsModal
          patientDirections={encounter.patientDirections || ''}
          patientInstructions={encounter.patientInstructions || ''}
          isOpen={visitInstructionModalIsOpen}
          onClose={() => setVisitInstructionModalIsOpen(false)}
        />
      )}
      <Card>
        <Card.Header background="gray">
          <HStack gap={4}>
            <Typography variant="title-medium" bold>
              {encounter.startTime &&
                moment(encounter.startTime).format('MMM DD, h:mma')}
            </Typography>
            {encounter.arrivalTime && (
              <>
                <Typography variant="title-medium" color="secondary">
                  |
                </Typography>
                <Typography variant="title-medium" color="secondary">
                  {t('arrival', {
                    arrivalTime: moment(encounter.arrivalTime).format('h:mma'),
                  })}
                </Typography>
              </>
            )}
          </HStack>
        </Card.Header>
        <Card.Body>
          <VStack gap={16} width="100%">
            <HStack gap={16} alignItems="center">
              <StyledAvatar
                alt={`${provider.firstName} ${provider.lastName}`}
                src={
                  (provider.previsitImageUrl &&
                    getPrevisitAsset(provider.previsitImageUrl)) ||
                  undefined
                }
              />
              <VStack gap={2}>
                <Typography variant="title-large" bold>
                  {formattedName}
                </Typography>
                <Typography variant="title-medium">
                  {formatProviderSpecialties(provider.specialties)}
                </Typography>
              </VStack>
            </HStack>
            <VStack gap={8}>
              <Typography variant="paragraph-medium">
                {practice.name}
              </Typography>
              <Typography variant="paragraph-medium" color="secondary">
                <StyledLink
                  href={generateGoogleMapsDirectionURL({ ...practice })}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {formattedAddress}
                </StyledLink>
              </Typography>
            </VStack>
            {(isCheckingIn || encounter.patientInstructions) && <Divider />}
            {(encounter.patientDirections || encounter.patientInstructions) && (
              <Button
                variant="outlined"
                size="medium"
                fullWidth
                onClick={() => setVisitInstructionModalIsOpen(true)}
              >
                {t('visitType', { type: t('instructions').toLowerCase() })}
              </Button>
            )}
            {isCheckingIn && (
              <AlertMessage
                header={t('incorrectLocationHeader')}
                subheader={t('incorrectLocationSubheader')}
                variant="info"
              />
            )}
          </VStack>
        </Card.Body>
      </Card>
    </>
  );
};

export default AppointmentDetailsCard;

const StyledAvatar = styled(Avatar)`
  min-height: 80px !important;
  min-width: 80px !important;
`;

const StyledLink = styled.a`
  color: inherit;
  font-size: inherit;
  font-weight: inherit;
`;
